import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import GavelIcon from "@material-ui/icons/Gavel";
import MessageIcon from "@material-ui/icons/Message";
import SettingsEthernetIcon from "@material-ui/icons/SettingsEthernet";
import TuneIcon from "@material-ui/icons/Tune";

import useSettings from "../../hooks/useSettings";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  hero: {
    padding: theme.spacing(2.5),
    borderRadius: 20,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,64,175,0.26) 100%)"
        : "linear-gradient(135deg, rgba(248,250,252,1) 0%, rgba(219,234,254,0.88) 100%)",
    border: `1px solid ${fade(theme.palette.divider, 0.72)}`,
    boxShadow: "none",
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  helperText: {
    opacity: 0.74,
    marginTop: theme.spacing(0.75),
    lineHeight: 1.6,
    maxWidth: 720,
  },
  heroChips: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
      marginTop: theme.spacing(1),
    },
  },
  summaryCard: {
    padding: theme.spacing(2),
    borderRadius: 18,
    minHeight: 118,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${fade(theme.palette.divider, 0.82)}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.75rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(1),
  },
  summaryFootnote: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  sectionPaper: {
    borderRadius: 20,
    border: `1px solid ${fade(theme.palette.divider, 0.86)}`,
    boxShadow: "none",
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2.25, 2.5),
    borderBottom: `1px solid ${fade(theme.palette.divider, 0.72)}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      padding: theme.spacing(2),
    },
  },
  sectionHeaderLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  sectionIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: fade(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  sectionTitle: {
    fontWeight: 600,
  },
  sectionDescription: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.6,
    maxWidth: 760,
  },
  sectionMetaChip: {
    border: `1px solid ${fade(theme.palette.divider, 0.88)}`,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  rows: {
    display: "flex",
    flexDirection: "column",
  },
  settingRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.35fr) minmax(280px, 1fr)",
    gap: theme.spacing(2.5),
    padding: theme.spacing(2.25, 2.5),
    alignItems: "center",
    "& + &": {
      borderTop: `1px solid ${fade(theme.palette.divider, 0.68)}`,
    },
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.75, 2),
      gap: theme.spacing(1.5),
    },
  },
  textRow: {
    alignItems: "flex-start",
  },
  settingMeta: {
    minWidth: 0,
  },
  settingTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.75),
  },
  settingDescription: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    maxWidth: 620,
  },
  controlColumn: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  switchShell: {
    minHeight: 56,
    border: `1px solid ${fade(theme.palette.divider, 0.92)}`,
    borderRadius: 14,
    padding: theme.spacing(0.75, 1.25),
    backgroundColor:
      theme.palette.type === "dark"
        ? fade(theme.palette.background.default, 0.38)
        : fade(theme.palette.action.hover, 0.55),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
  },
  switchCopy: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.4),
    minWidth: 0,
  },
  switchLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.66,
  },
  switchValue: {
    fontWeight: 600,
    lineHeight: 1.2,
  },
  field: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 14,
      "& fieldset": {
        borderColor: fade(theme.palette.divider, 0.92),
      },
      "&:hover fieldset": {
        borderColor: fade(theme.palette.primary.main, 0.35),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.12)}`,
      },
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiOutlinedInput-inputMultiline": {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
    },
  },
  statusRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  syncChip: {
    fontWeight: 600,
  },
}));

const STATUS_OPTIONS = [
  {
    value: "disabled",
    label: i18n.t("settings.settings.options.disabled"),
  },
  {
    value: "enabled",
    label: i18n.t("settings.settings.options.enabled"),
  },
];

const BOOLEAN_OPTIONS = [
  {
    value: false,
    label: i18n.t("settings.settings.options.disabled"),
  },
  {
    value: true,
    label: i18n.t("settings.settings.options.enabled"),
  },
];

const isBinaryOptions = (options = []) =>
  Array.isArray(options) &&
  options.length === 2 &&
  ((options[0].value === "disabled" && options[1].value === "enabled") ||
    (options[0].value === false && options[1].value === true));

const renderChipColor = (value) => {
  if (
    value === true ||
    value === "enabled" ||
    value === "company" ||
    value === "queue"
  ) {
    return { backgroundColor: "#dcfce7", color: "#166534" };
  }

  if (value === "connection") {
    return { backgroundColor: "#dbeafe", color: "#1d4ed8" };
  }

  if (value === "bot") {
    return { backgroundColor: "#ede9fe", color: "#6d28d9" };
  }

  return { backgroundColor: "#e5e7eb", color: "#374151" };
};

const resolveOptionLabel = (value, options = []) => {
  const matchedOption = options.find((option) => option.value === value);

  if (matchedOption) {
    return matchedOption.label;
  }

  if (value === true) {
    return i18n.t("settings.settings.options.enabled");
  }

  if (value === false) {
    return i18n.t("settings.settings.options.disabled");
  }

  return value ? String(value) : "-";
};

export default function Options(props) {
  const { oldSettings, settings, scheduleTypeChanged, user } = props;
  const classes = useStyles();

  const [userRating, setUserRating] = useState("disabled");
  const [scheduleType, setScheduleType] = useState("disabled");
  const [chatBotType, setChatBotType] = useState("text");
  const [loadingUserRating, setLoadingUserRating] = useState(false);
  const [loadingScheduleType, setLoadingScheduleType] = useState(false);

  const [userCreation, setUserCreation] = useState("disabled");
  const [loadingUserCreation, setLoadingUserCreation] = useState(false);

  const [SendGreetingAccepted, setSendGreetingAccepted] = useState("enabled");
  const [loadingSendGreetingAccepted, setLoadingSendGreetingAccepted] =
    useState(false);

  const [UserRandom, setUserRandom] = useState("enabled");
  const [loadingUserRandom, setLoadingUserRandom] = useState(false);

  const [SettingsTransfTicket, setSettingsTransfTicket] = useState("enabled");
  const [loadingSettingsTransfTicket, setLoadingSettingsTransfTicket] =
    useState(false);

  const [AcceptCallWhatsapp, setAcceptCallWhatsapp] = useState("enabled");
  const [loadingAcceptCallWhatsapp, setLoadingAcceptCallWhatsapp] =
    useState(false);

  const [sendSignMessage, setSendSignMessage] = useState("enabled");
  const [loadingSendSignMessage, setLoadingSendSignMessage] = useState(false);

  const [sendGreetingMessageOneQueues, setSendGreetingMessageOneQueues] =
    useState("enabled");
  const [
    loadingSendGreetingMessageOneQueues,
    setLoadingSendGreetingMessageOneQueues,
  ] = useState(false);

  const [sendQueuePosition, setSendQueuePosition] = useState("enabled");
  const [loadingSendQueuePosition, setLoadingSendQueuePosition] =
    useState(false);

  const [sendFarewellWaitingTicket, setSendFarewellWaitingTicket] =
    useState("enabled");
  const [
    loadingSendFarewellWaitingTicket,
    setLoadingSendFarewellWaitingTicket,
  ] = useState(false);

  const [acceptAudioMessageContact, setAcceptAudioMessageContact] =
    useState("enabled");
  const [
    loadingAcceptAudioMessageContact,
    setLoadingAcceptAudioMessageContact,
  ] = useState(false);

  const [enableLGPD, setEnableLGPD] = useState("disabled");
  const [loadingEnableLGPD, setLoadingEnableLGPD] = useState(false);

  const [lgpdMessage, setLGPDMessage] = useState("");
  const [loadinglgpdMessage, setLoadingLGPDMessage] = useState(false);

  const [lgpdLink, setLGPDLink] = useState("");
  const [loadingLGPDLink, setLoadingLGPDLink] = useState(false);

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState("disabled");
  const [loadingLGPDDeleteMessage, setLoadingLGPDDeleteMessage] =
    useState(false);

  const [lgpdConsent, setLGPDConsent] = useState("disabled");
  const [loadingLGPDConsent, setLoadingLGPDConsent] = useState(false);

  const [lgpdHideNumber, setLGPDHideNumber] = useState("disabled");
  const [loadingLGPDHideNumber, setLoadingLGPDHideNumber] = useState(false);

  const [requiredTag, setRequiredTag] = useState("enabled");
  const [loadingRequiredTag, setLoadingRequiredTag] = useState(false);

  const [closeTicketOnTransfer, setCloseTicketOnTransfer] = useState(false);
  const [loadingCloseTicketOnTransfer, setLoadingCloseTicketOnTransfer] =
    useState(false);

  const [directTicketsToWallets, setDirectTicketsToWallets] = useState(false);
  const [
    loadingDirectTicketsToWallets,
    setLoadingDirectTicketsToWallets,
  ] = useState(false);

  const defaultTransferMessage =
    "Seu Atendimento foi Transferido para o setor " +
    "${queue.name},Aguarde atendimento por favor...";

  const [transferMessage, setTransferMessage] = useState(
    defaultTransferMessage
  );
  const [loadingTransferMessage, setLoadingTransferMessage] = useState(false);

  const [greetingAcceptedMessage, setGreetingAcceptedMessage] = useState("");
  const [loadingGreetingAcceptedMessage, setLoadingGreetingAcceptedMessage] =
    useState(false);

  const [AcceptCallWhatsappMessage, setAcceptCallWhatsappMessage] =
    useState("");
  const [
    loadingAcceptCallWhatsappMessage,
    setLoadingAcceptCallWhatsappMessage,
  ] = useState(false);

  const [sendQueuePositionMessage, setSendQueuePositionMessage] = useState("");
  const [
    loadingSendQueuePositionMessage,
    setLoadingSendQueuePositionMessage,
  ] = useState(false);

  const [showNotificationPending, setShowNotificationPending] = useState(false);
  const [
    loadingShowNotificationPending,
    setLoadingShowNotificationPending,
  ] = useState(false);

  const [apiTranscription, setApiTranscription] = useState("");
  const [loadingApiTranscription, setLoadingApiTranscription] = useState(false);

  const { update: updateUserCreation } = useSettings();
  const { update } = useCompanySettings();

  const isSuper = () => Boolean(user?.super);

  useEffect(() => {
    if (Array.isArray(oldSettings) && oldSettings.length) {
      const userPar = oldSettings.find((setting) => setting.key === "userCreation");
      if (userPar) {
        setUserCreation(userPar.value);
      }

      const transcriptionSetting = oldSettings.find(
        (setting) => setting.key === "apiTranscription"
      );
      if (transcriptionSetting) {
        setApiTranscription(transcriptionSetting.value);
      }
    }
  }, [oldSettings]);

  useEffect(() => {
    for (const [key, value] of Object.entries(settings || {})) {
      if (key === "userRating") setUserRating(value);
      if (key === "scheduleType") setScheduleType(value);
      if (key === "chatBotType") setChatBotType(value);
      if (key === "acceptCallWhatsapp") setAcceptCallWhatsapp(value);
      if (key === "userRandom") setUserRandom(value);
      if (key === "sendGreetingMessageOneQueues") {
        setSendGreetingMessageOneQueues(value);
      }
      if (key === "sendSignMessage") setSendSignMessage(value);
      if (key === "sendFarewellWaitingTicket") {
        setSendFarewellWaitingTicket(value);
      }
      if (key === "sendGreetingAccepted") setSendGreetingAccepted(value);
      if (key === "sendQueuePosition") setSendQueuePosition(value);
      if (key === "acceptAudioMessageContact") {
        setAcceptAudioMessageContact(value);
      }
      if (key === "enableLGPD") setEnableLGPD(value);
      if (key === "requiredTag") setRequiredTag(value);
      if (key === "lgpdDeleteMessage") setLGPDDeleteMessage(value);
      if (key === "lgpdHideNumber") setLGPDHideNumber(value);
      if (key === "lgpdConsent") setLGPDConsent(value);
      if (key === "lgpdMessage") setLGPDMessage(value);
      if (key === "sendMsgTransfTicket") setSettingsTransfTicket(value);
      if (key === "lgpdLink") setLGPDLink(value);
      if (key === "DirectTicketsToWallets") setDirectTicketsToWallets(value);
      if (key === "closeTicketOnTransfer") setCloseTicketOnTransfer(value);
      if (key === "transferMessage") setTransferMessage(value);
      if (key === "greetingAcceptedMessage") {
        setGreetingAcceptedMessage(value);
      }
      if (key === "AcceptCallWhatsappMessage") {
        setAcceptCallWhatsappMessage(value);
      }
      if (key === "sendQueuePositionMessage") {
        setSendQueuePositionMessage(value);
      }
      if (key === "showNotificationPending") setShowNotificationPending(value);
    }
  }, [settings]);

  async function handleChangeUserCreation(value) {
    setUserCreation(value);
    setLoadingUserCreation(true);
    await updateUserCreation({ key: "userCreation", value });
    setLoadingUserCreation(false);
  }

  async function handleApiTranscription(value) {
    setApiTranscription(value);
    setLoadingApiTranscription(true);
    await updateUserCreation({ key: "apiTranscription", value });
    setLoadingApiTranscription(false);
  }

  async function handleChangeUserRating(value) {
    setUserRating(value);
    setLoadingUserRating(true);
    await update({ column: "userRating", data: value });
    setLoadingUserRating(false);
  }

  async function handleScheduleType(value) {
    setScheduleType(value);
    setLoadingScheduleType(true);
    await update({ column: "scheduleType", data: value });
    setLoadingScheduleType(false);
    if (typeof scheduleTypeChanged === "function") {
      scheduleTypeChanged(value);
    }
  }

  async function handleChatBotType(value) {
    setChatBotType(value);
    await update({ column: "chatBotType", data: value });
  }

  async function handleLGPDMessage(value) {
    setLGPDMessage(value);
    setLoadingLGPDMessage(true);
    await update({ column: "lgpdMessage", data: value });
    setLoadingLGPDMessage(false);
  }

  async function handletransferMessage(value) {
    setTransferMessage(value);
    setLoadingTransferMessage(true);
    await update({ column: "transferMessage", data: value });
    setLoadingTransferMessage(false);
  }

  async function handleGreetingAcceptedMessage(value) {
    setGreetingAcceptedMessage(value);
    setLoadingGreetingAcceptedMessage(true);
    await update({ column: "greetingAcceptedMessage", data: value });
    setLoadingGreetingAcceptedMessage(false);
  }

  async function handleAcceptCallWhatsappMessage(value) {
    setAcceptCallWhatsappMessage(value);
    setLoadingAcceptCallWhatsappMessage(true);
    await update({ column: "AcceptCallWhatsappMessage", data: value });
    setLoadingAcceptCallWhatsappMessage(false);
  }

  async function handlesendQueuePositionMessage(value) {
    setSendQueuePositionMessage(value);
    setLoadingSendQueuePositionMessage(true);
    await update({ column: "sendQueuePositionMessage", data: value });
    setLoadingSendQueuePositionMessage(false);
  }

  async function handleShowNotificationPending(value) {
    setShowNotificationPending(value);
    setLoadingShowNotificationPending(true);
    await update({ column: "showNotificationPending", data: value });
    setLoadingShowNotificationPending(false);
  }

  async function handleLGPDLink(value) {
    setLGPDLink(value);
    setLoadingLGPDLink(true);
    await update({ column: "lgpdLink", data: value });
    setLoadingLGPDLink(false);
  }

  async function handleLGPDDeleteMessage(value) {
    setLGPDDeleteMessage(value);
    setLoadingLGPDDeleteMessage(true);
    await update({ column: "lgpdDeleteMessage", data: value });
    setLoadingLGPDDeleteMessage(false);
  }

  async function handleLGPDConsent(value) {
    setLGPDConsent(value);
    setLoadingLGPDConsent(true);
    await update({ column: "lgpdConsent", data: value });
    setLoadingLGPDConsent(false);
  }

  async function handleLGPDHideNumber(value) {
    setLGPDHideNumber(value);
    setLoadingLGPDHideNumber(true);
    await update({ column: "lgpdHideNumber", data: value });
    setLoadingLGPDHideNumber(false);
  }

  async function handleSendGreetingAccepted(value) {
    setSendGreetingAccepted(value);
    setLoadingSendGreetingAccepted(true);
    await update({ column: "sendGreetingAccepted", data: value });
    setLoadingSendGreetingAccepted(false);
  }

  async function handleUserRandom(value) {
    setUserRandom(value);
    setLoadingUserRandom(true);
    await update({ column: "userRandom", data: value });
    setLoadingUserRandom(false);
  }

  async function handleSettingsTransfTicket(value) {
    setSettingsTransfTicket(value);
    setLoadingSettingsTransfTicket(true);
    await update({ column: "sendMsgTransfTicket", data: value });
    setLoadingSettingsTransfTicket(false);
  }

  async function handleAcceptCallWhatsapp(value) {
    setAcceptCallWhatsapp(value);
    setLoadingAcceptCallWhatsapp(true);
    await update({ column: "acceptCallWhatsapp", data: value });
    setLoadingAcceptCallWhatsapp(false);
  }

  async function handleSendSignMessage(value) {
    setSendSignMessage(value);
    setLoadingSendSignMessage(true);
    await update({ column: "sendSignMessage", data: value });
    localStorage.setItem("sendSignMessage", value === "enabled" ? true : false);
    setLoadingSendSignMessage(false);
  }

  async function handleSendGreetingMessageOneQueues(value) {
    setSendGreetingMessageOneQueues(value);
    setLoadingSendGreetingMessageOneQueues(true);
    await update({ column: "sendGreetingMessageOneQueues", data: value });
    setLoadingSendGreetingMessageOneQueues(false);
  }

  async function handleSendQueuePosition(value) {
    setSendQueuePosition(value);
    setLoadingSendQueuePosition(true);
    await update({ column: "sendQueuePosition", data: value });
    setLoadingSendQueuePosition(false);
  }

  async function handleSendFarewellWaitingTicket(value) {
    setSendFarewellWaitingTicket(value);
    setLoadingSendFarewellWaitingTicket(true);
    await update({ column: "sendFarewellWaitingTicket", data: value });
    setLoadingSendFarewellWaitingTicket(false);
  }

  async function handleAcceptAudioMessageContact(value) {
    setAcceptAudioMessageContact(value);
    setLoadingAcceptAudioMessageContact(true);
    await update({ column: "acceptAudioMessageContact", data: value });
    setLoadingAcceptAudioMessageContact(false);
  }

  async function handleEnableLGPD(value) {
    setEnableLGPD(value);
    setLoadingEnableLGPD(true);
    await update({ column: "enableLGPD", data: value });
    setLoadingEnableLGPD(false);
  }

  async function handleRequiredTag(value) {
    setRequiredTag(value);
    setLoadingRequiredTag(true);
    await update({ column: "requiredTag", data: value });
    setLoadingRequiredTag(false);
  }

  async function handleCloseTicketOnTransfer(value) {
    setCloseTicketOnTransfer(value);
    setLoadingCloseTicketOnTransfer(true);
    await update({ column: "closeTicketOnTransfer", data: value });
    setLoadingCloseTicketOnTransfer(false);
  }

  async function handleDirectTicketsToWallets(value) {
    setDirectTicketsToWallets(value);
    setLoadingDirectTicketsToWallets(true);
    await update({ column: "DirectTicketsToWallets", data: value });
    setLoadingDirectTicketsToWallets(false);
  }

  const summary = useMemo(() => {
    const operationalFlags = [
      userRating === "enabled",
      SendGreetingAccepted === "enabled",
      UserRandom === "enabled",
      SettingsTransfTicket === "enabled",
      AcceptCallWhatsapp === "enabled",
      sendSignMessage === "enabled",
      sendGreetingMessageOneQueues === "enabled",
      sendQueuePosition === "enabled",
      sendFarewellWaitingTicket === "enabled",
      acceptAudioMessageContact === "enabled",
      requiredTag === "enabled",
      showNotificationPending === true,
      closeTicketOnTransfer === true,
      directTicketsToWallets === true,
    ];

    const enabledCount = operationalFlags.filter(Boolean).length;

    return {
      enabledCount,
      lgpd: enableLGPD === "enabled" ? "Ativa" : "Inativa",
      schedule:
        scheduleType === "company"
          ? i18n.t("settings.settings.options.companyManagement")
          : scheduleType === "queue"
            ? i18n.t("settings.settings.options.queueManagement")
            : scheduleType === "connection"
              ? i18n.t("settings.settings.options.connectionManagement")
              : i18n.t("settings.settings.options.disabled"),
      chatbot: chatBotType === "bot" ? "Bot" : "Texto",
    };
  }, [
    AcceptCallWhatsapp,
    SendGreetingAccepted,
    SettingsTransfTicket,
    UserRandom,
    acceptAudioMessageContact,
    chatBotType,
    closeTicketOnTransfer,
    directTicketsToWallets,
    enableLGPD,
    requiredTag,
    scheduleType,
    sendFarewellWaitingTicket,
    sendGreetingMessageOneQueues,
    sendQueuePosition,
    sendSignMessage,
    showNotificationPending,
    userRating,
  ]);

  const renderSyncChip = (loading) => (
    <Chip
      size="small"
      className={classes.syncChip}
      label={loading ? i18n.t("settings.settings.options.updating") : "Sincronizado"}
      icon={loading ? <AutorenewIcon fontSize="small" /> : undefined}
      style={
        loading
          ? { backgroundColor: "#fef3c7", color: "#92400e" }
          : { backgroundColor: "#e2e8f0", color: "#475569" }
      }
    />
  );

  const renderSection = ({ icon, title, description, countLabel, children }) => (
    <Paper className={classes.sectionPaper} elevation={0}>
      <Box className={classes.sectionHeader}>
        <Box className={classes.sectionHeaderLeft}>
          <Box className={classes.sectionIconWrap}>{icon}</Box>
          <Box>
            <Typography variant="h6" className={classes.sectionTitle}>
              {title}
            </Typography>
            <Typography variant="body2" className={classes.sectionDescription}>
              {description}
            </Typography>
          </Box>
        </Box>
        <Chip
          size="small"
          label={countLabel}
          className={classes.sectionMetaChip}
        />
      </Box>
      <Box className={classes.rows}>{children}</Box>
    </Paper>
  );

  const renderSelectRow = ({
    title,
    description,
    value,
    onChange,
    loading,
    options,
  }) => (
    <Box key={title} className={classes.settingRow}>
      <Box className={classes.settingMeta}>
        <Typography variant="body1" className={classes.settingTitle}>
          {title}
        </Typography>
        <Typography variant="body2" className={classes.settingDescription}>
          {description}
        </Typography>
      </Box>

      <Box className={classes.controlColumn}>
        {isBinaryOptions(options) ? (
          <Box className={classes.switchShell}>
            <Box className={classes.switchCopy}>
              <Typography className={classes.switchLabel}>Status atual</Typography>
              <Typography variant="body2" className={classes.switchValue}>
                {resolveOptionLabel(value, options)}
              </Typography>
            </Box>

            <Switch
              color="primary"
              checked={value === true || value === "enabled"}
              onChange={(event) =>
                onChange(
                  options[0].value === "disabled"
                    ? event.target.checked
                      ? "enabled"
                      : "disabled"
                    : event.target.checked
                )
              }
            />
          </Box>
        ) : (
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            value={value}
            onChange={(event) => onChange(event.target.value)}
          >
            {options.map((option) => (
              <MenuItem key={String(option.value)} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box className={classes.statusRow}>{renderSyncChip(loading)}</Box>
      </Box>
    </Box>
  );

  const renderTextRow = ({
    title,
    description,
    value,
    onChange,
    loading,
    label,
    rows = 3,
    multiline = true,
    type = "text",
    required = false,
    placeholder = "",
  }) => (
    <Box key={title} className={`${classes.settingRow} ${classes.textRow}`}>
      <Box className={classes.settingMeta}>
        <Typography variant="body1" className={classes.settingTitle}>
          {title}
        </Typography>
        <Typography variant="body2" className={classes.settingDescription}>
          {description}
        </Typography>
      </Box>

      <Box className={classes.controlColumn}>
        <TextField
          className={classes.field}
          fullWidth
          label={label}
          variant="outlined"
          margin="dense"
          multiline={multiline}
          rows={multiline ? rows : undefined}
          type={type}
          required={required}
          value={value || ""}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />

        <Box className={classes.statusRow}>{renderSyncChip(loading)}</Box>
      </Box>
    </Box>
  );

  const coreOptions = [
    isSuper()
      ? {
          title: i18n.t("settings.settings.options.creationCompanyUser"),
          description:
            "Controla se o ambiente pode criar novas empresas e usuarios administrativos diretamente pelo painel.",
          value: userCreation,
          onChange: handleChangeUserCreation,
          loading: loadingUserCreation,
          options: STATUS_OPTIONS,
        }
      : null,
    {
      title: i18n.t("settings.settings.options.evaluations"),
      description:
        "Define se o cliente pode avaliar o atendimento apos o encerramento.",
      value: userRating,
      onChange: handleChangeUserRating,
      loading: loadingUserRating,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.officeScheduling"),
      description:
        "Escolhe o nivel em que as regras de expediente serao administradas.",
      value: scheduleType,
      onChange: handleScheduleType,
      loading: loadingScheduleType,
      options: [
        {
          value: "disabled",
          label: i18n.t("settings.settings.options.disabled"),
        },
        {
          value: "queue",
          label: i18n.t("settings.settings.options.queueManagement"),
        },
        {
          value: "company",
          label: i18n.t("settings.settings.options.companyManagement"),
        },
        {
          value: "connection",
          label: i18n.t("settings.settings.options.connectionManagement"),
        },
      ],
    },
    {
      title: i18n.t("settings.settings.options.chatBotType"),
      description:
        "Define como o bot apresenta as interacoes automatizadas ao cliente.",
      value: chatBotType,
      onChange: handleChatBotType,
      loading: false,
      options: [
        { value: "text", label: "Texto" },
        { value: "bot", label: "Bot" },
      ],
    },
    {
      title: i18n.t("settings.settings.options.userRandom"),
      description:
        "Distribui atendimentos entre usuarios disponiveis de forma automatica.",
      value: UserRandom,
      onChange: handleUserRandom,
      loading: loadingUserRandom,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.requiredTag"),
      description:
        "Exige classificacao por tag antes de concluir o fluxo do ticket.",
      value: requiredTag,
      onChange: handleRequiredTag,
      loading: loadingRequiredTag,
      options: STATUS_OPTIONS,
    },
  ].filter(Boolean);

  const automationOptions = [
    {
      title: i18n.t("settings.settings.options.sendGreetingAccepted"),
      description:
        "Envia mensagem quando o atendimento e assumido por um usuario.",
      value: SendGreetingAccepted,
      onChange: handleSendGreetingAccepted,
      loading: loadingSendGreetingAccepted,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.sendMsgTransfTicket"),
      description:
        "Dispara aviso automatico ao mover um ticket para outro setor.",
      value: SettingsTransfTicket,
      onChange: handleSettingsTransfTicket,
      loading: loadingSettingsTransfTicket,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.acceptCallWhatsapp"),
      description:
        "Controla se chamadas recebidas no WhatsApp entram no fluxo operacional.",
      value: AcceptCallWhatsapp,
      onChange: handleAcceptCallWhatsapp,
      loading: loadingAcceptCallWhatsapp,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.sendSignMessage"),
      description:
        "Inclui assinatura automatica do atendente nas mensagens de resposta.",
      value: sendSignMessage,
      onChange: handleSendSignMessage,
      loading: loadingSendSignMessage,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.sendGreetingMessageOneQueues"),
      description:
        "Envia saudacao automatica quando existe apenas uma fila disponivel.",
      value: sendGreetingMessageOneQueues,
      onChange: handleSendGreetingMessageOneQueues,
      loading: loadingSendGreetingMessageOneQueues,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.sendQueuePosition"),
      description:
        "Mostra ao contato a sua posicao de espera durante a fila.",
      value: sendQueuePosition,
      onChange: handleSendQueuePosition,
      loading: loadingSendQueuePosition,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.sendFarewellWaitingTicket"),
      description:
        "Envia mensagem de despedida quando o ticket permanece aguardando.",
      value: sendFarewellWaitingTicket,
      onChange: handleSendFarewellWaitingTicket,
      loading: loadingSendFarewellWaitingTicket,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.acceptAudioMessageContact"),
      description:
        "Permite o recebimento de audios enviados pelos contatos.",
      value: acceptAudioMessageContact,
      onChange: handleAcceptAudioMessageContact,
      loading: loadingAcceptAudioMessageContact,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.closeTicketOnTransfer"),
      description:
        "Fecha o ticket atual ao transferir o atendimento para outro setor.",
      value: closeTicketOnTransfer,
      onChange: handleCloseTicketOnTransfer,
      loading: loadingCloseTicketOnTransfer,
      options: BOOLEAN_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.DirectTicketsToWallets"),
      description:
        "Encaminha automaticamente o cliente para a carteira responsavel quando existir correspondencia disponivel.",
      value: directTicketsToWallets,
      onChange: handleDirectTicketsToWallets,
      loading: loadingDirectTicketsToWallets,
      options: BOOLEAN_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.options.showNotificationPending"),
      description:
        "Exibe alertas visuais para tickets que continuam pendentes.",
      value: showNotificationPending,
      onChange: handleShowNotificationPending,
      loading: loadingShowNotificationPending,
      options: BOOLEAN_OPTIONS,
    },
  ];

  const complianceOptions = [
    {
      title: i18n.t("settings.settings.options.enableLGPD"),
      description:
        "Liga o conjunto de regras de privacidade e consentimento da operacao.",
      value: enableLGPD,
      onChange: handleEnableLGPD,
      loading: loadingEnableLGPD,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.LGPD.obfuscateMessageDelete"),
      description:
        "Define se mensagens apagadas pelo contato devem ser ocultadas no historico.",
      value: lgpdDeleteMessage,
      onChange: handleLGPDDeleteMessage,
      loading: loadingLGPDDeleteMessage,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.LGPD.alwaysConsent"),
      description:
        "Solicita consentimento do cliente durante a jornada de atendimento.",
      value: lgpdConsent,
      onChange: handleLGPDConsent,
      loading: loadingLGPDConsent,
      options: STATUS_OPTIONS,
    },
    {
      title: i18n.t("settings.settings.LGPD.obfuscatePhoneUser"),
      description:
        "Oculta parte do numero do contato para perfis operacionais.",
      value: lgpdHideNumber,
      onChange: handleLGPDHideNumber,
      loading: loadingLGPDHideNumber,
      options: STATUS_OPTIONS,
    },
  ];

  const messageOptions = [
    {
      title: "Mensagem de transferencia",
      description: "Texto enviado quando um atendimento muda de setor.",
      value: transferMessage,
      onChange: handletransferMessage,
      loading: loadingTransferMessage,
      label: i18n.t("settings.settings.customMessages.transferMessage"),
      required: SettingsTransfTicket === "enabled",
    },
    {
      title: "Mensagem de atendimento assumido",
      description:
        "Texto exibido ao cliente quando um atendente assume o caso.",
      value: greetingAcceptedMessage,
      onChange: handleGreetingAcceptedMessage,
      loading: loadingGreetingAcceptedMessage,
      label: i18n.t("settings.settings.customMessages.greetingAcceptedMessage"),
      required: SendGreetingAccepted === "enabled",
    },
    {
      title: "Mensagem para chamadas recebidas",
      description:
        "Resposta padrao quando a operacao nao aceita chamadas pelo WhatsApp.",
      value: AcceptCallWhatsappMessage,
      onChange: handleAcceptCallWhatsappMessage,
      loading: loadingAcceptCallWhatsappMessage,
      label: i18n.t("settings.settings.customMessages.AcceptCallWhatsappMessage"),
      required: AcceptCallWhatsapp === "disabled",
    },
    {
      title: "Mensagem de posicao na fila",
      description:
        "Template enviado para informar a fila de espera do contato.",
      value: sendQueuePositionMessage,
      onChange: handlesendQueuePositionMessage,
      loading: loadingSendQueuePositionMessage,
      label: i18n.t("settings.settings.customMessages.sendQueuePositionMessage"),
      required: sendQueuePosition === "enabled",
    },
  ];

  const visibleComplianceOptions = complianceOptions.filter(
    (item) =>
      enableLGPD === "enabled" ||
      item.title === i18n.t("settings.settings.options.enableLGPD")
  );

  return (
    <div className={classes.root}>
      <Paper className={classes.hero} elevation={0}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography className={classes.eyebrow}>
              Configuracao da operacao
            </Typography>
            <Typography variant="h6" className={classes.heroTitle}>
              Opcoes operacionais
            </Typography>
            <Typography variant="body2" className={classes.helperText}>
              Revise atendimento, automacoes, privacidade e mensagens em uma
              leitura mais enxuta, com menos blocos visuais e mais foco no que
              realmente muda a operacao.
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className={classes.heroChips}>
              <Chip
                label={`LGPD: ${summary.lgpd}`}
                style={renderChipColor(enableLGPD)}
              />
              <Chip
                label={`Expediente: ${summary.schedule}`}
                style={renderChipColor(scheduleType)}
              />
              <Chip
                label={`Chatbot: ${summary.chatbot}`}
                style={renderChipColor(chatBotType)}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.summaryCard} elevation={0}>
            <Typography className={classes.summaryLabel}>
              Recursos operacionais ativos
            </Typography>
            <Typography className={classes.summaryValue}>
              {summary.enabledCount}
            </Typography>
            <Typography className={classes.summaryFootnote}>
              Ajustes ativos entre fila, atendimento e governanca.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.summaryCard} elevation={0}>
            <Typography className={classes.summaryLabel}>
              Politica LGPD
            </Typography>
            <Typography className={classes.summaryValue}>
              {summary.lgpd}
            </Typography>
            <Typography className={classes.summaryFootnote}>
              Estado atual das regras de privacidade da operacao.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.summaryCard} elevation={0}>
            <Typography className={classes.summaryLabel}>Expediente</Typography>
            <Typography className={classes.summaryValue}>
              {summary.schedule}
            </Typography>
            <Typography className={classes.summaryFootnote}>
              Onde a gestao de horarios fica centralizada.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.summaryCard} elevation={0}>
            <Typography className={classes.summaryLabel}>
              Experiencia do bot
            </Typography>
            <Typography className={classes.summaryValue}>
              {summary.chatbot}
            </Typography>
            <Typography className={classes.summaryFootnote}>
              Forma atual de apresentacao das interacoes automatizadas.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {renderSection({
        icon: <TuneIcon fontSize="small" />,
        title: "Base da operacao",
        description:
          "Controles estruturais que definem como a operacao funciona no dia a dia.",
        countLabel: `${coreOptions.length} controles`,
        children: coreOptions.map(renderSelectRow),
      })}

      {renderSection({
        icon: <SettingsEthernetIcon fontSize="small" />,
        title: "Automacoes e fila",
        description:
          "Ajustes que influenciam mensagens automaticas, distribuicao, espera e transicoes do atendimento.",
        countLabel: `${automationOptions.length} ajustes`,
        children: automationOptions.map(renderSelectRow),
      })}

      {renderSection({
        icon: <GavelIcon fontSize="small" />,
        title: "Privacidade e governanca",
        description:
          "Regras de consentimento, protecao de historico e ocultacao de dados sensiveis.",
        countLabel: `${visibleComplianceOptions.length + (enableLGPD === "enabled" ? 2 : 0)} regras`,
        children: (
          <>
            {visibleComplianceOptions.map(renderSelectRow)}
            {enableLGPD === "enabled"
              ? renderTextRow({
                  title: "Mensagem inicial de privacidade",
                  description:
                    "Texto apresentado ao contato ao iniciar o fluxo de consentimento.",
                  value: lgpdMessage,
                  onChange: handleLGPDMessage,
                  loading: loadinglgpdMessage,
                  label: i18n.t("settings.settings.LGPD.welcome"),
                })
              : null}
            {enableLGPD === "enabled"
              ? renderTextRow({
                  title: "Link da politica",
                  description:
                    "Endereco usado para direcionar o cliente aos termos de privacidade.",
                  value: lgpdLink,
                  onChange: handleLGPDLink,
                  loading: loadingLGPDLink,
                  label: i18n.t("settings.settings.LGPD.linkLGPD"),
                  rows: 1,
                  multiline: false,
                })
              : null}
          </>
        ),
      })}

      {renderSection({
        icon: <MessageIcon fontSize="small" />,
        title: "Mensagens personalizadas",
        description:
          "Ajuste os principais templates do sistema sem depender de cards isolados para cada campo.",
        countLabel: `${messageOptions.length + 1} campos`,
        children: (
          <>
            {messageOptions.map(renderTextRow)}
            {renderTextRow({
              title: "Chave de transcricao",
              description:
                "Credencial usada para recursos de transcricao automatica por IA.",
              value: apiTranscription,
              onChange: handleApiTranscription,
              loading: loadingApiTranscription,
              label: "Chave da API de Transcricao (OpenAI)",
              type: "password",
              multiline: false,
              placeholder: "coloque sua chave da openai...",
            })}
          </>
        ),
      })}
    </div>
  );
}
